import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { getUsers } from "../../lib/user";
import { db } from "../../config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import CountrySelectInput from "../shared/countrySelectInput";
import { generatePDF } from "../shared/pdf/generatePDF";
import SideBar from "../sidebar/SideBar";
import Header from "../header/Header";
import { useParams } from "react-router-dom";
import { FormatDate } from "../../lib/dateFormat";
import { AmountFormat } from "../../lib/helper/amountFormat";

const ViewQuote = () => {
  const obj = {
    ative: true,
    content: "Quote List",
  };

  const { id } = useParams();
  const [Total, setTotal] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    status: "pending",
    quoteNumber: "",
    companyName: "",
    name: "", // Use default value if customerData is null
    email: "",
    postalCode: "",
    address: "",
    country: "",
    loadAddress: "",
    loadCountry: "",
    loadCity: "",
    loadPostalCode: "",
    loadAddress_CompanyName: "",
    unloadAddress: "",
    unloadCountry: "",
    unloadCity: "",
    unloadPostalCode: "",
    unloadAddress_CompanyName: "",
    modeOfTransfer: "",
    cargoValue: { value: "", currency: "USD" },
    cifValue: { value: "", currency: "USD" },
    pcsPackage: [
      {
        quantity: "",
        weight: { value: "", unit: "Kg" },
        length: { value: "", unit: "cm" },
        width: { value: "", unit: "cm" },
        height: { value: "", unit: "cm" },
      },
    ],
    totalWeight: totalWeight,
    description: "",
    services: [
      {
        numberOfItem: "",
        description: "",
        exclVAT: "",
      },
    ],
    total: Total,
    subTotal: 0,
    vatPercentage: 0,
    vatAmount: 0,
    currency: "USD",
  });
  const [pcsOfPkg, setPcsOfPkg] = useState(formData.pcsPackage.length);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userDocRef = doc(db, "quotes", id);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setFormData(data);
        } else {
          toast.error("Quote data not found");
        }
      } catch (error) {
        console.error("Error fetching Quote data:", error);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    // Calculate Sub Total
    const subTotal = formData.services.reduce((acc, service) => {
      const totalForService =
        parseFloat(service.numberOfItem || 1) *
        parseFloat(service.exclVAT || 0);
      return acc + totalForService;
    }, 0);

    // Calculate VAT Amount
    const vatPercentage = formData.vatPercentage || 0;
    const vatAmount =
      vatPercentage === 0 ? 0 : (subTotal * vatPercentage) / 100;

    // Calculate Total including VAT
    const total = vatPercentage === 0 ? subTotal : subTotal + vatAmount;

    // Format total to display only two decimal points
    const formattedTotal = total.toFixed(2);

    // Update state
    setTotal(formattedTotal);
    setFormData((prevData) => ({
      ...prevData,
      subTotal: subTotal.toFixed(2), // Update subTotal
      vatAmount: vatAmount.toFixed(2), // Update vatAmount
      total: formattedTotal, // Update total
    }));
  }, [formData.services, formData.vatPercentage]);
  // console.log(formData)

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePcsOfPkgInput = (index, field, e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      const newData = { ...prevData };
      const pcsPackage = [...newData.pcsPackage];

      // Ensure that pcsPackage array has an item at the specified index
      if (pcsPackage[index]) {
        // Update the specific nested property (quantity, weight, length, width, height)
        if (field === "quantity") {
          pcsPackage[index][field] = value;
        } else {
          pcsPackage[index][field].value = value;
        }
      }

      newData.pcsPackage = pcsPackage;
      return newData;
    });
  };

  const handleUnitChange = (index, field, e) => {
    const { value } = e.target;

    setFormData((prevData) => {
      const newData = { ...prevData };
      const pcsPackage = [...newData.pcsPackage];

      // Ensure that pcsPackage array has an item at the specified index
      if (pcsPackage[index]) {
        // Update the unit value for the specified field
        pcsPackage[index][field].unit = value;
      }

      newData.pcsPackage = pcsPackage;
      return newData;
    });
  };

  useEffect(() => {
    const total = formData.pcsPackage.reduce((acc, pcs) => {
      const weightValue =
        parseFloat(pcs.quantity || 1) * parseFloat(pcs.weight.value || 0);
      return acc + weightValue;
    }, 0);

    setTotalWeight(total); // Update totalWeight state with numeric value
    setFormData((prevData) => ({
      ...prevData,
      totalWeight: total, // Set totalWeight field in formData with numeric value
    }));
  }, [formData.pcsPackage, totalWeight]);

  const handleRemovePcsOfpackage = () => {
    setFormData((prevData) => ({
      ...prevData,
      pcsPackage: prevData.pcsPackage.slice(0, -1), // Remove the last added package
    }));
  };

  const handleAddMorePcsOfpackage = () => {
    setFormData((prevData) => ({
      ...prevData,
      pcsPackage: [
        ...prevData.pcsPackage,
        {
          quantity: "", // Initialize quantity for each new item
          weight: { value: "", unit: "Kg" },
          length: { value: "", unit: "cm" },
          width: { value: "", unit: "cm" },
          height: { value: "", unit: "cm" },
        },
      ],
    }));
  };

  const handleServiceInputChange = (index, field, e) => {
    const { value } = e.target;

    setFormData((prevData) => {
      const updatedServices = [...prevData.services];
      updatedServices[index][field] = value;

      return {
        ...prevData,
        services: updatedServices,
      };
    });
  };
  // console.log(formData)

  const handleAddData = async (e) => {
    e.preventDefault(); // Prevent form submission from reloading the page
    // console.log(formData);
    try {
      setLoading(true);
      const userDocRef = doc(db, "quotes", id);
      await updateDoc(userDocRef, formData);
      window.location.reload();
      toast.success("Quotes data updated successfully!");
      // Consider resetting form or redirecting instead of reloading
      // setUserData({ name: '', email: '', password: '' });
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Error saving data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMore = () => {
    setFormData((prevData) => ({
      ...prevData,
      services: [...prevData.services, {}], // Update 'documents' to 'services'
    }));
  };

  const handleRemoveService = () => {
    setFormData((prevData) => ({
      ...prevData,
      services: prevData.services.slice(0, -1), // Remove the last added service
    }));
  };

  const downloadPDF = async () => {
    const currentDate = new Date().toISOString().slice(0, 10);
    const blob = await generatePDF(formData);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const fileName = `quotation-${FormatDate(currentDate)}.pdf`;
    link.download = fileName;
    link.click();
  };

  // console.log(formData);

  return (
    <>
      <div className="flex">
        <div className="hidden h-full border-r sm:block ">
          <SideBar obj={obj} />
        </div>
        <div className="w-full">
          <Header title="View Quote" />
          <div id="data" className="py-5">
            <h2 className="mx-[30px] font-bold text-xl mt-4">Quote Details </h2>
            <div className=" mx-[30px] grid md:grid-cols-2 grid-cols-1 gap-3">
              <div className="flex flex-col gap-1 w-1/2  py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="quoteNumber"
                >
                  Quote Number
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.quoteNumber}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label className="w-full text-sm font-normal " htmlFor="name">
                  Name
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.name}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal "
                  htmlFor="companyname"
                >
                  Email Address
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.email}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal "
                  htmlFor="addressone"
                >
                  Postal Code
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.postalCode}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal "
                  htmlFor="addresst"
                >
                  Address
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.address}
                </div>
              </div>
            </div>

            <div className="mx-[30px] ">
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Load Address
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.loadAddress}
                </div>
              </div>
            </div>
            <div className="mx-[30px] grid md:grid-cols-4 grid-cols-1 gap-3">
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Load Country
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.loadCountry}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2 ">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Load City
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.loadCity}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Load Postal Code
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.loadPostalCode}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Load Company Name
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.loadAddress_CompanyName}
                </div>
              </div>
            </div>

            <div className="mx-[30px] ">
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal "
                  htmlFor="country"
                >
                  Unload Address
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.unloadAddress}
                </div>
              </div>
            </div>

            <div className="mx-[30px] grid md:grid-cols-4 grid-cols-1 gap-3">
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  <span className="text-red-500">*</span>Unload country
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.unloadCountry}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2 ">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  unload City
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.unloadCity}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  unload Postal Code
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.unloadPostalCode}
                </div>
              </div>
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Unload Company Name
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.unloadAddress_CompanyName}
                </div>
              </div>
            </div>

            <div className="mx-[30px]">
              <div className="flex flex-col gap-1 py-2 ">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="loadAddress"
                >
                  Transportation Mode
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue">
                  {formData.modeOfTransfer}
                </div>
              </div>
            </div>

            <div className="mx-[30px] grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="cargoValue"
                >
                  Cargo Value
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                  {formData.cargoValue.value} - {formData.cargoValue.currency}
                </div>
              </div>

              <div className="flex flex-col gap-1 py-2">
                <label
                  className="w-full text-sm font-normal"
                  htmlFor="cifValue"
                >
                  CIF Value
                </label>
                <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                  {formData.cifValue.value} - {formData.cifValue.currency}
                </div>
              </div>
            </div>

            <h2 className="mx-[30px] font-bold text-xl mt-4">Goods</h2>

            {formData.pcsPackage.map((formItem, index) => (
              <div
                key={index}
                className="mx-[30px] flex-wrap justify-between flex items-center gap-5"
              >
                {/* Weight */}
                <div className="w-full lg:w-[10%] flex flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`amount-${index}`}
                  >
                    Quantity
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.quantity}{" "}
                  </div>
                </div>
                <div className="flex w-full lg:w-1/5  flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`weight-${index}`}
                  >
                    Weight<span className="text-red">*</span>
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.weight.value} {formItem.weight.unit}
                  </div>
                </div>

                {/* Length */}
                <div className="flex w-full lg:w-1/5  flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`length-${index}`}
                  >
                    Length<span className="text-red">*</span>
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.length.value} {formItem.length.unit}
                  </div>
                </div>
                {/* Width */}
                <div className="flex w-full lg:w-1/5  flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`width-${index}`}
                  >
                    Width<span className="text-red">*</span>
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.width.value} {formItem.width.unit}
                  </div>
                </div>
                {/* Height */}
                <div className="flex w-full lg:w-1/5  flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`height-${index}`}
                  >
                    Height<span className="text-red">*</span>
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.height.value} {formItem.height.unit}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-between gap-5 items-center px-5 py-2">
              <div className="px-5 flex  items-center gap-5">
                <p className="text-[14px]"> Total Weight:</p>
                <p className="text-[14px] font-extrabold">
                  {formData.totalWeight ? formData.totalWeight : "00"}
                </p>
              </div>
            </div>

            <h2 className="mx-[30px] font-bold text-xl mt-4">Services</h2>

            {formData.services.map((formItem, index) => (
              <div
                className="flex flex-col lg:flex-row justify-between  gap-5 mx-[30px]"
                key={index}
              >
                <div className="w-full lg:w-[20%] flex flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`amount-${index}`}
                  >
                    Number
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.numberOfItem}
                  </div>
                </div>
                <div className="w-full lg:w-[50%] flex flex-col gap-1 py-2">
                  <label
                    className="w-full text-sm font-normal"
                    htmlFor={`description-${index}`}
                  >
                    Description
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.description}
                  </div>
                </div>
                <div className="w-full lg:w-[10%] flex flex-col gap-1 py-2">
                  <label
                    className="w-full text-[gray] text-sm font-normal"
                    htmlFor={`exclVAT-${index}`}
                  >
                    excl.VAT
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formItem.exclVAT}
                  </div>
                </div>
                <div className="lg:w-[20%] flex gap-2">
                  <div className="w-32">
                    <h1 className="mt-2">Total</h1>
                    <p className="text-[gray] w-full p-1 text-sm border rounded-md">
                      {(
                        parseFloat(formItem.exclVAT || 0) *
                        parseFloat(formItem.numberOfItem || 1)
                      ).toFixed(2)}
                    </p>
                  </div>
                  <div className="">
                    <h1 className="mt-2">Currency</h1> {/* Fixed typo here */}
                    <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                      {formData.currency}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="mx-[30px] flex flex-col lg:flex-row gap-2 ">
              <div className="w-full lg:w-[60%] bg-[#FBFCFF] p-4 rounded-lg  border border-cus-blue ">
                {/* <h1 className=" text-[16px] font-bold ">
                            Transport Warehouse - Hoofdrop
                        </h1>

                        <p className="text-[gray] ">
                            Selling Price: $0.00 excl VAT
                        </p> */}
                <div className="flex items-center  w-full lg:w-1/2  gap-1 py-2">
                  <label className="w-full text-base font-medium">
                    VAT Percentage :<span className="text-red"></span>
                  </label>
                  <div className="w-full h-8 p-2 text-sm border rounded-md outline-none">
                    {formData.vatPercentage}
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-[40%] bg-[#FBFCFF] p-4 border rounded-lg border-cus-blue">
                <div className="flex justify-between">
                  <div>
                    <p className="text-[14px]">Sub Total</p>
                  </div>
                  <div className="">
                    <p className="text-[14px] font-extrabold">
                      {AmountFormat(formData.subTotal, formData.currency)}
                    </p>
                  </div>
                </div>

                {(() => {
                  const discount = formData.discount || formData.tradeOSData?.discount;
                  if (!discount || !(discount.amount > 0)) return null;
                  return (
                    <div className="flex justify-between mt-1 text-orange-600">
                      <div>
                        <p className="text-[14px] font-medium">
                          Discount ({discount.type === 'percentage' ? `${Math.abs(discount.value)}%` : 'Fixed'})
                        </p>
                      </div>
                      <div className="">
                        <p className="text-[14px] font-extrabold">
                          - {AmountFormat(discount.amount, formData.currency)}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                <div className="flex justify-between mt-1 ">
                  <div>
                    <p className="text-[14px]">Total VAT</p>
                  </div>
                  <div className="">
                    <p className="text-[14px] font-extrabold ">
                      {AmountFormat(formData.vatAmount, formData.currency)}
                    </p>
                  </div>
                </div>

                <div className="flex justify-between mt-4 border-t border-cus-blue ">
                  <div className="mt-2">
                    <p className="text-[14px]">Total</p>
                  </div>
                  <div className="mt-2">
                    <p className="text-[14px] font-extrabold ">
                      {AmountFormat(formData.total, formData.currency)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewQuote;
